import { NgModule } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AgmCoreModule } from '@agm/core';
import { AgmSnazzyInfoWindowModule } from '@agm/snazzy-info-window';
import { StarRatingModule } from 'angular-star-rating';

import { AppRoutingModule } from '../routing/app-routing.module';
import { AppMaterialModule } from '../app-material.module';
import { AirportService } from './airport.service';
import { AirportDetailComponent } from './airport-detail/airport-detail.component';
import { AirportActivityComponent } from './airport-activity/airport-activity.component';
import { AirportOverviewComponent } from './airport-overview/airport-overview.component';
import { SharedModule } from '../shared/shared.module';
import { BusinessSummaryComponent } from './business-summary/business-summary.component';
import { AirportPageComponent } from './airport-page/airport-page.component';
import { ActivityDetailComponent } from './activity-detail/activity-detail.component';
import { ChartPanelComponent } from './chart-panel/chart-panel.component';
import { SkyVectorChartComponent } from './skyvector-chart/skyvector-chart.component';
import { RemarksComponent } from './remarks/remarks.component';
import { NoiseAbatementComponent } from './noise-abatement/noise-abatement.component';
import { WeatherModule } from '../weather/weather.module';
import { MetarSectionComponent } from './metar-section/metar-section.component';
import { ContactComponent } from './contact/contact.component';
import { ContactListingComponent } from './contact-listing/contact-listing.component';
import { ServicesComponent } from './services/services.component';
import { OperationsComponent } from './operations/operations.component';
import { TafSectionComponent } from './taf-section/taf-section.component';
import { NavaidComponent } from './navaid/navaid.component';
import { NavaidListingComponent } from './navaid-listing/navaid-listing.component';
import { ProcedureListingComponent } from './procedure-listing/procedure-listing.component';
import { ProcedureComponent } from './procedure/procedure.component';
import { RemarkListComponent } from './remark-list/remark-list.component';
import { LocalPanelComponent } from './local-panel/local-panel.component';
import { LocalAttractionsComponent } from './local-attractions/local-attractions.component';
import { BusinessListingComponent } from './business-listing/business-listing.component';
import { RunwayDetailComponent } from './runway-detail/runway-detail.component';
import { RunwayListingComponent } from './runway-listing/runway-listing.component';
import { CommunicationCardComponent } from './communication-card/communication-card.component';
import { AirportAlertComponent } from './airport-alert/airport-alert.component';
import { OverviewHeaderComponent } from './overview-header/overview-header.component';
import { ActivityPanelComponent } from './activity-panel/activity-panel.component';
import { LocalActivitiesComponent } from './local-activities/local-activities.component';
import { AirportFeeSummaryComponent } from './airport-fee-summary/airport-fee-summary.component';
import { TravelAffiliationsComponent } from './travel-affiliations/travel-affiliations.component';
import { FlyingclubListingComponent } from './flyingclub-listing/flyingclub-listing.component';
import { FlyingclubSummaryComponent } from './flyingclub-summary/flyingclub-summary.component';
import { AddBusinessDialogComponent } from './add-business-dialog/add-business-dialog.component';
import { MatRadioModule } from '@angular/material/radio';
import { EditPatternAltitudeComponent } from './edit-pattern-altitude/edit-pattern-altitude.component';
import { EditOverviewComponent } from './edit-overview/edit-overview.component';
import { OverviewService } from './service/overview.service';
import { EditAlertComponent } from './edit-alert/edit-alert.component';
import { AlertService } from './service/alert.service';
import { DeleteAlertComponent } from './delete-alert/delete-alert.component';
import { PatternAltService } from './service/pattern-alt.service';
import { EditPilotControlledLightingComponent } from './edit-pilot-controlled-lighting/edit-pilot-controlled-lighting.component';
import { PilotControlledLightingService } from './service/pilot-controlled-lighting.service';
import { DeletePatternAltitudeComponent } from './delete-pattern-altitude/delete-pattern-altitude.component';
import { DeletePilotControlledLightingComponent } from './delete-pilot-controlled-lighting/delete-pilot-controlled-lighting.component';
import { EditNoiseAbatementComponent } from './edit-noise-abatement/edit-noise-abatement.component';
import { NoiseAbatementService } from './service/noise-abatement.service';
import { DeleteNoiseAbatementComponent } from './delete-noise-abatement/delete-noise-abatement.component';
import { EditRemarkComponent } from './edit-remark/edit-remark.component';
import { RemarkService } from './service/remark.service';
import { DeleteRemarkComponent } from './delete-remark/delete-remark.component';
import { AirportFlightDeckButtonComponent } from './airport-flight-deck-button/airport-flight-deck-button.component';
import { AdminAirportComponent } from './admin-airport/admin-airport.component';
import { EditLocalAttractionComponent } from './edit-local-attraction/edit-local-attraction.component';
import { DeleteLocalAttractionComponent } from './delete-local-attraction/delete-local-attraction.component';
import { LocalAttractionService } from './service/local-attraction.service';
import { EditGaTransientComponent } from './edit-ga-transient/edit-ga-transient.component';
import { AirportWebcamsComponent } from './airport-webcams/airport-webcams.component';
import { EditWebcamComponent } from './edit-webcam/edit-webcam.component';
import { DeleteWebcamComponent } from './delete-webcam/delete-webcam.component';
import { DistanceCalculatorComponent } from './distance-calculator/distance-calculator.component';
import { MatExpansionModule, MatStepperModule } from '@angular/material';
import { EditCampingComponent } from './edit-camping/edit-camping.component';
import { AirportIslandDetailComponent } from './airport-island-detail/airport-island-detail.component';
import { EditIslandOverviewComponent } from './edit-island-overview/edit-island-overview.component';
import { EditNavaidComponent } from './edit-navaid/edit-navaid.component';
import { DeleteNavaidComponent } from './delete-navaid/delete-navaid.component';
import { NavaidService } from './service/navaid.service';
import { EditCommunicationComponent } from './edit-communication/edit-communication.component';
import { DeleteCommunicationComponent } from './delete-communication/delete-communication.component';
import { CommunicationService } from './service/communication.service';
import { EditRunwayComponent } from './edit-runway/edit-runway.component';
import { RunwayService } from './service/runway.service';
import { DeleteRunwayComponent } from './delete-runway/delete-runway.component';
import { EditTakeoffLandAreaComponent } from './edit-takeoff-land-area/edit-takeoff-land-area.component';
import { DeleteTakeoffLandAreaComponent } from './delete-takeoff-land-area/delete-takeoff-land-area.component';
import { EditAreaLightingComponent } from './edit-area-lighting/edit-area-lighting.component';
import { DeleteAreaLightingComponent } from './delete-area-lighting/delete-area-lighting.component';
import { EditAreaObstacleComponent } from './edit-area-obstacle/edit-area-obstacle.component';
import { DeleteAreaObstacleComponent } from './delete-area-obstacle/delete-area-obstacle.component';
import { EditOperationsComponent } from './edit-operations/edit-operations.component';
import { WhatsnewComponent } from '../whatsnew/whatsnew.component';
import { SuggestAChangeService } from './service/suggest-a-change.service';
import { SubmitNewBusinessComponent } from './submit-new-business/submit-new-business.component';
import { UserSubmitNewBusinessComponent } from './user-submit-new-business/user-submit-new-business.component';
import { AnalyticsService } from './service/google-analytics-4.service';
import { TfrsComponent } from './tfrs/tfrs.component';
import { NotamsComponent } from './notams/notams.component';
import { TfrAlertComponent } from './tfr-alert/tfr-alert.component';
import { TfrDetailsComponent } from './tfr-details/tfr-details.component';
import { NotamDetailsComponent } from './notam-details/notam-details.component';


@NgModule({
  imports: [
    CommonModule,
    SharedModule,
    BrowserModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    AgmCoreModule,
    AgmSnazzyInfoWindowModule,
    AppRoutingModule,
    AppMaterialModule,
    WeatherModule,
    MatRadioModule,
    StarRatingModule.forRoot(),
    MatStepperModule,
    MatExpansionModule
  ],
  declarations: [
    AirportDetailComponent,
    AirportOverviewComponent,
    AirportActivityComponent,
    BusinessSummaryComponent,
    NoiseAbatementComponent,
    AirportPageComponent,
    ActivityDetailComponent,
    ChartPanelComponent,
    SkyVectorChartComponent,
    RemarksComponent,
    MetarSectionComponent,
    ContactComponent,
    ContactListingComponent,
    ServicesComponent,
    OperationsComponent,
    TafSectionComponent,
    NavaidComponent,
    NavaidListingComponent,
    ProcedureListingComponent,
    ProcedureComponent,
    LocalPanelComponent,
    LocalAttractionsComponent,
    RemarkListComponent,
    BusinessListingComponent,
    RunwayDetailComponent,
    RunwayListingComponent,
    CommunicationCardComponent,
    AirportAlertComponent,
    OverviewHeaderComponent,
    ActivityPanelComponent,
    LocalActivitiesComponent,
    AirportFeeSummaryComponent,
    TravelAffiliationsComponent,
    FlyingclubListingComponent,
    FlyingclubSummaryComponent,
    AddBusinessDialogComponent,
    EditPatternAltitudeComponent,
    EditOverviewComponent,
    EditAlertComponent,
    DeleteAlertComponent,
    EditPilotControlledLightingComponent,
    DeletePatternAltitudeComponent,
    DeletePilotControlledLightingComponent,
    EditNoiseAbatementComponent,
    DeleteNoiseAbatementComponent,
    EditRemarkComponent,
    DeleteRemarkComponent,
    AirportFlightDeckButtonComponent,
    AdminAirportComponent,
    EditLocalAttractionComponent,
    DeleteLocalAttractionComponent,
    EditGaTransientComponent,
    AirportWebcamsComponent,
    EditWebcamComponent,
    DeleteWebcamComponent,
    DistanceCalculatorComponent,
    EditCampingComponent,
    AirportIslandDetailComponent,
    EditIslandOverviewComponent,
    EditNavaidComponent,
    DeleteNavaidComponent,
    EditCommunicationComponent,
    DeleteCommunicationComponent,
    EditRunwayComponent,
    DeleteRunwayComponent,
    EditTakeoffLandAreaComponent,
    DeleteTakeoffLandAreaComponent,
    EditAreaLightingComponent,
    DeleteAreaLightingComponent,
    EditAreaObstacleComponent,
    DeleteAreaObstacleComponent,
    EditOperationsComponent,
    WhatsnewComponent,
    SubmitNewBusinessComponent,
    UserSubmitNewBusinessComponent,
    TfrsComponent,
    NotamsComponent,
    TfrAlertComponent,
    TfrDetailsComponent,
    NotamDetailsComponent
  ],
  providers: [
    DecimalPipe,
    AirportService,
    OverviewService,
    AlertService,
    PatternAltService,
    PilotControlledLightingService,
    NoiseAbatementService,
    RemarkService,
    LocalAttractionService,
    NavaidService,
    CommunicationService,
    RunwayService,
    SuggestAChangeService,
    AnalyticsService
  ],
  entryComponents: [
    AddBusinessDialogComponent,
    EditPatternAltitudeComponent,
    EditOverviewComponent,
    EditAlertComponent,
    DeleteAlertComponent,
    EditPilotControlledLightingComponent,
    DeletePatternAltitudeComponent,
    DeletePilotControlledLightingComponent,
    EditNoiseAbatementComponent,
    DeleteNoiseAbatementComponent,
    EditRemarkComponent,
    DeleteRemarkComponent,
    AdminAirportComponent,
    EditLocalAttractionComponent,
    DeleteLocalAttractionComponent,
    EditGaTransientComponent,
    EditWebcamComponent,
    DeleteWebcamComponent,
    EditCampingComponent,
    EditIslandOverviewComponent,
    EditNavaidComponent,
    DeleteNavaidComponent,
    EditCommunicationComponent,
    DeleteCommunicationComponent,
    EditRunwayComponent,
    DeleteRunwayComponent,
    EditTakeoffLandAreaComponent,
    DeleteTakeoffLandAreaComponent,
    EditAreaLightingComponent,
    DeleteAreaLightingComponent,
    EditAreaObstacleComponent,
    DeleteAreaObstacleComponent,
    EditOperationsComponent,
    WhatsnewComponent,
    SubmitNewBusinessComponent,
    UserSubmitNewBusinessComponent,
    TfrDetailsComponent,
    NotamDetailsComponent
  ]
})
export class AirportModule {
}
